"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { ArrowRight, Users, Smartphone, Globe, Network } from "@/components/icons";
import Link from "next/link";
import { buttonVariants, EyebrowBadge, HeroGlow, PricingCtaSection } from "@/components/ui/";
import { CAREERS_URL } from "@/lib/constants";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { cn } from "@/lib/utils";
import ReviewsSection from "@/components/sections/shared/reviews";
import BadgesSection from "@/components/sections/shared/badges";
import { stats, milestones, teamMembers, whyItems } from "./data";
import { useUptime } from "@/hooks/use-uptime";
import { TeamMemberCard } from "./parts";
import { STATS, ROUTES, DEVELOPERS_URL } from "@/lib/constants";

const MotionDiv = motion.div;

export default function AboutSection() {
  const prefersReducedMotion = useReducedMotion();
  const uptime = useUptime();
  const uptimeDisplay = `${uptime.toFixed(3)}%`;

  const fadeUp = (delay = 0) => ({
    initial: prefersReducedMotion ? {} : { opacity: 0, y: 24 },
    whileInView: prefersReducedMotion ? {} : { opacity: 1, y: 0 },
    viewport: scrollRevealViewport,
    transition: { duration: 0.55, delay },
  });

  return (
    <main id="main-content" tabIndex={-1}>
      <section
        className="border-border relative overflow-y-clip border-b pt-32 pb-24"
        aria-labelledby="about-heading"
      >
        <HeroGlow align="left" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)}>
            <EyebrowBadge>About Proxidize</EyebrowBadge>
          </MotionDiv>

          <MotionDiv {...fadeUp(0.08)}>
            <h1
              id="about-heading"
              className="text-foreground mb-8 max-w-4xl text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
            >
              Democratizing access <br></br> to the internet.
            </h1>
          </MotionDiv>

          <MotionDiv {...fadeUp(0.12)}>
            <p className="text-foreground mb-4 max-w-3xl text-base leading-relaxed font-medium">
              Proxidize is a managed cloud proxy provider offering{" "}
              <Link href={ROUTES.productMobileProxies} className="text-[--green] hover:underline">
                mobile proxies
              </Link>{" "}
              and{" "}
              <Link
                href={ROUTES.productResidentialProxies}
                className="text-[--green] hover:underline"
              >
                residential proxies
              </Link>{" "}
              for web scraping, data collection, automation, SEO monitoring, market research, and AI
              data pipelines.
            </p>
          </MotionDiv>

          <MotionDiv {...fadeUp(0.16)} className="grid max-w-4xl gap-8 sm:grid-cols-1">
            <p className="text-muted-foreground light:text-foreground/80 text-base leading-relaxed">
              Proxidize was founded in 2022 with a simple belief: businesses, developers, and data
              teams should be able to access public web data reliably, securely, and at scale. What
              started with a book became a platform, and what started as a platform now powers{" "}
              {STATS.customers} of teams across {STATS.countries} countries.
            </p>
          </MotionDiv>

          <MotionDiv {...fadeUp(0.24)} className="mt-10 flex flex-wrap gap-3">
            <Link
              href="/contact"
              className={cn(buttonVariants({ variant: "default", size: "lg" }))}
            >
              Talk to our team
              <ArrowRight size={16} className="ml-2" aria-hidden="true" />
            </Link>
            <Link
              href="/proxy-server/mobile-proxies"
              className={cn(buttonVariants({ variant: "outline", size: "lg" }))}
            >
              Explore our products
            </Link>
          </MotionDiv>
        </div>
      </section>

      <section className="py-section-sm border-border border-b" aria-label="Company statistics">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 lg:grid-cols-4 lg:gap-12">
            {stats.map((stat, i) => (
              <MotionDiv key={stat.label} {...fadeUp(i * 0.08)} className="flex flex-col gap-1">
                <span className="text-foreground text-4xl font-bold tracking-tight">
                  {stat.value === "UPTIME_PLACEHOLDER" ? uptimeDisplay : stat.value}
                </span>
                <span className="text-muted-foreground text-sm">{stat.label}</span>
              </MotionDiv>
            ))}
          </div>
        </div>
      </section>

      <section className="py-section border-border border-b" aria-labelledby="offers-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)} className="mb-3">
            <p className="text-muted-foreground text-sm font-medium">What Proxidize Offers Today</p>
          </MotionDiv>
          <MotionDiv {...fadeUp(0.06)} className="mb-12 max-w-2xl">
            <h2
              id="offers-heading"
              className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl"
            >
              Managed cloud proxies, ready to deploy.
            </h2>
          </MotionDiv>

          <div className="grid gap-5 sm:grid-cols-3">
            <MotionDiv
              {...fadeUp(0)}
              className="border-border bg-surface-1 flex flex-col gap-4 rounded-xl border p-6"
            >
              <div className="bg-muted/40 flex h-10 w-10 items-center justify-center rounded-lg">
                <Smartphone size={18} className="text-foreground" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-foreground mb-2 text-lg font-semibold">Mobile Proxies</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Premium pooled mobile IPs across dozens of locations worldwide. Rotation, sticky
                  sessions, city targeting, and ASN/ISP targeting — all managed, no hardware
                  required.
                </p>
              </div>
              <Link
                href={ROUTES.productMobileProxies}
                className="mt-auto flex items-center gap-1.5 text-sm font-medium text-[--green] hover:underline"
              >
                Explore Mobile Proxies
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </MotionDiv>

            <MotionDiv
              {...fadeUp(0.08)}
              className="border-border bg-surface-1 flex flex-col gap-4 rounded-xl border p-6"
            >
              <div className="bg-muted/40 flex h-10 w-10 items-center justify-center rounded-lg">
                <Globe size={18} className="text-foreground" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-foreground mb-2 text-lg font-semibold">Residential Proxies</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Millions of ethically sourced residential IPs across {STATS.countries} countries,
                  with city and ISP-level targeting and full bandwidth rollover.
                </p>
              </div>
              <Link
                href={ROUTES.productResidentialProxies}
                className="mt-auto flex items-center gap-1.5 text-sm font-medium text-[--green] hover:underline"
              >
                Explore Residential Proxies
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </MotionDiv>

            <MotionDiv
              {...fadeUp(0.16)}
              className="border-border bg-surface-1 flex flex-col gap-4 rounded-xl border p-6"
            >
              <div className="bg-muted/40 flex h-10 w-10 items-center justify-center rounded-lg">
                <Network size={18} className="text-foreground" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-foreground mb-2 text-lg font-semibold">
                  Managed Cloud Infrastructure
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  No hardware to buy, no DIY setup, no manual proxy maintenance. Deploy a proxy pool
                  in minutes via dashboard or API and scale on demand.
                </p>
              </div>
              <Link
                href={DEVELOPERS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto flex items-center gap-1.5 text-sm font-medium text-[--green] hover:underline"
              >
                Read the docs
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </MotionDiv>
          </div>
        </div>
      </section>

      <section className="py-section border-border border-b" aria-labelledby="story-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)} className="mb-14 max-w-xl">
            <p className="text-muted-foreground mb-3 text-sm font-medium">Our Story</p>
            <h2
              id="story-heading"
              className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl"
            >
              Proxidize is a managed cloud proxy provider trusted by {STATS.customers} of teams.
            </h2>
          </MotionDiv>

          <MotionDiv
            {...fadeUp(0.08)}
            className="border-border bg-surface-1 relative mb-16 overflow-hidden rounded-2xl border p-8 sm:p-10"
          >
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.05]"
              aria-hidden="true"
              style={{
                background: "radial-gradient(ellipse at top left, #2B72FF 0%, transparent 60%)",
              }}
            />
            <p className="text-foreground max-w-3xl text-lg leading-relaxed font-medium sm:text-xl">
              What began as research into proxy technology became Proxidize: a managed cloud proxy
              provider offering ready-to-use{" "}
              <Link href={ROUTES.productMobileProxies} className="text-[--green] hover:underline">
                mobile
              </Link>{" "}
              and{" "}
              <Link
                href={ROUTES.productResidentialProxies}
                className="text-[--green] hover:underline"
              >
                residential proxies
              </Link>{" "}
              to teams in {STATS.countries} countries. Here&apos;s how we got here.
            </p>
            <p className="text-muted-foreground mt-5 max-w-3xl text-base leading-relaxed">
              Proxidize originally began with proprietary proxy hardware and self-hosted
              infrastructure. Today, our focus is managed cloud mobile and residential proxies.
              Legacy products remain supported for existing users where applicable.
            </p>
          </MotionDiv>

          {/* Timeline */}
          <div className="relative">
            <div
              className="bg-border absolute top-0 bottom-0 left-[calc(50%-1px)] hidden w-px lg:block"
              aria-hidden="true"
            />
            <div className="space-y-10 lg:space-y-0">
              {milestones.map((m, i) => (
                <MotionDiv
                  key={m.year}
                  {...fadeUp(i * 0.1)}
                  className={cn(
                    "items-center lg:grid lg:grid-cols-2 lg:gap-16",
                    i % 2 === 0 ? "" : "lg:[&>*:first-child]:order-2"
                  )}
                >
                  <div
                    className={cn(
                      "relative lg:py-8",
                      i % 2 === 0 ? "lg:pr-16 lg:text-right" : "lg:pl-16"
                    )}
                  >
                    <div
                      className={cn(
                        "mb-3 inline-flex items-center gap-2",
                        i % 2 === 0 ? "lg:flex-row-reverse" : ""
                      )}
                    >
                      <span className="text-muted-foreground text-xs font-bold">{m.year}</span>
                      <span className="bg-border h-px w-8" aria-hidden="true" />
                    </div>
                    <h3 className="text-foreground mb-2 text-lg font-semibold">{m.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{m.description}</p>
                  </div>

                  <div
                    className="bg-background absolute left-1/2 hidden h-4 w-4 -translate-x-1/2 items-center justify-center rounded-full border-2 lg:flex"
                    style={{ borderColor: "rgba(91,140,255,0.6)" }}
                  >
                    <div
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: "var(--brand-green)" }}
                    />
                  </div>

                  <div />
                </MotionDiv>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. Core values ───────────────────────────────────────────────── */}
      <section className="py-section border-border border-b" aria-labelledby="values-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)} className="mb-8 max-w-xl">
            <p className="text-muted-foreground mb-3 text-sm font-medium">Our Core Tenets</p>
            <h2
              id="values-heading"
              className="text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl"
            >
              The principles that drive everything we build.
            </h2>
            <p className="text-muted-foreground mb-2 text-base leading-relaxed">
              Democratizing access to the internet means removing every barrier between people and
              the data they need.
            </p>
            <p className="text-muted-foreground mb-2 text-base leading-relaxed">
              That covers cost, complexity, and control. We deliver managed cloud proxy access
              that&apos;s affordable enough for an individual, simple enough to set up in minutes,
              and powerful enough for enterprise-scale operations. We write our own software, we run
              our own network, and we maintain direct control over every layer of the stack.
            </p>
            <p className="text-muted-foreground mb-2 text-base leading-relaxed">
              Everything we build comes back to one question: does this make data more accessible?
            </p>
          </MotionDiv>
        </div>
      </section>

      {/* ── 5. Team section ──────────────────────────────────────────────── */}
      <section className="py-section border-border border-b" aria-labelledby="team-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)} className="mb-14 max-w-xl">
            <p className="text-muted-foreground mb-3 text-sm font-medium">The Team</p>
            <h2
              id="team-heading"
              className="text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl"
            >
              Meet the people behind Proxidize.
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed">
              A multidisciplinary team of engineers, product specialists, and network experts united
              by a single mission: make mobile proxy technology accessible to everyone.
            </p>
          </MotionDiv>

          <div className="grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
            {teamMembers.map((member, i) => (
              <MotionDiv key={member.name} {...fadeUp(i * 0.07)}>
                <TeamMemberCard member={member} />
              </MotionDiv>
            ))}
          </div>

          <MotionDiv
            {...fadeUp(0.2)}
            className="border-border bg-surface-1 mt-12 flex flex-col items-start gap-6 rounded-2xl border p-8 sm:flex-row sm:items-center"
          >
            <div className="bg-muted/40 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl">
              <Users size={22} className="text-foreground" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h3 className="text-foreground mb-1 text-lg font-semibold">Join the team</h3>
              <p className="text-muted-foreground text-sm">
                Join a remote-first team solving some of the hardest problems in proxy
                infrastructure, data access, and network engineering.
              </p>
            </div>
            <Link
              href={CAREERS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(buttonVariants({ variant: "outline", size: "sm" }), "shrink-0")}
            >
              View open roles
              <ArrowRight size={14} className="ml-1.5" aria-hidden="true" />
            </Link>
          </MotionDiv>
        </div>
      </section>

      <ReviewsSection />

      <BadgesSection />

      <section className="py-section border-border border-b" aria-labelledby="why-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <MotionDiv {...fadeUp(0)} className="mx-auto mb-14 max-w-2xl text-center">
            <p className="text-muted-foreground mb-3 text-sm font-medium">Why Choose Proxidize</p>
            <h2
              id="why-heading"
              className="text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl"
            >
              Built differently. Built better.
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed">
              We didn&rsquo;t build another proxy provider. We reimagined the entire experience —
              from how you connect to how you scale.
            </p>
          </MotionDiv>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {whyItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <MotionDiv
                  key={item.title}
                  {...fadeUp(i * 0.07)}
                  className="border-border bg-surface-1 hover:border-border/60 rounded-xl border p-6 transition-colors"
                >
                  <div className="bg-muted/40 mb-4 flex h-9 w-9 items-center justify-center rounded-lg">
                    <Icon size={16} className="text-foreground" aria-hidden="true" />
                  </div>
                  <h3 className="text-foreground mb-1.5 text-lg font-semibold">
                    {item.title === "UPTIME_PLACEHOLDER"
                      ? `${uptimeDisplay} Uptime Guarantee`
                      : item.title}
                  </h3>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    {item.description}
                  </p>
                </MotionDiv>
              );
            })}
          </div>
        </div>
      </section>

      {/* The site-wide closing band. This was hand-rolled here with its own
          glow (an off-brand #2B72FF), a smaller headline and no perk row, even
          though its buttons were already the standard trial + sales pair. */}
      <PricingCtaSection
        id="about-cta-heading"
        heading="Start using Proxidize today."
        body={`Join ${STATS.customers} teams using Proxidize for web scraping, data collection, and automation at scale.`}
      />
    </main>
  );
}
